import type { MouseEventHandler } from 'react';
import { useCallback, useRef } from 'react';
import { FRAME_WIDTH } from '../constants';
import type { CurrentFrameMarkerRefT, EditAnnotationPanelRefT, HoveredColumnRefT, OnScrollT } from '../types';
import { getFrameIndex } from '../utils/get-frame-index';
type PropsT = {
  columnStripRef: React.RefObject<HTMLDivElement>;
  rowStripRef: React.RefObject<HTMLDivElement>;
  hoveredColumnRef: React.RefObject<HoveredColumnRefT>;
  currentFrameMarkerRef: React.RefObject<CurrentFrameMarkerRefT>;
  editAnnotationPanelRef: React.RefObject<EditAnnotationPanelRefT>;
  totalFrames: number;
};
type ScrollPositionT = {
  scrollLeft: number;
  scrollTop: number;
};
export function useHandleScrollMouseEvents({
  columnStripRef,
  rowStripRef,
  hoveredColumnRef,
  currentFrameMarkerRef,
  editAnnotationPanelRef,
  totalFrames
}: PropsT) {
  const rafRef = useRef<number | null>(null);
  const mouseXPositionRef = useRef<number | null>(null);
  const scrollRef = useRef<ScrollPositionT>({
    scrollLeft: 0,
    scrollTop: 0
  });
  const showHoveredColumn = useCallback((scrollLeft: number, mouseXPosition: number) => {
    const columnIndex = getFrameIndex({
      scrollLeft,
      mouseXPosition,
      frameWidth: FRAME_WIDTH,
      maxFrames: totalFrames
    });
    hoveredColumnRef.current?.show(columnIndex, scrollLeft);
  }, [hoveredColumnRef, totalFrames]);

  // Keeps row and column headers in sync with the scroll position
  const sync = useCallback((scrollLeft: number, scrollTop: number) => {
    columnStripRef.current && (columnStripRef.current.style.transform = `translateX(${-scrollLeft}px)`);
    rowStripRef.current && (rowStripRef.current.style.transform = `translateY(${-scrollTop}px)`);

    // If there is mouse position from move event, update the hovered column
    if (mouseXPositionRef.current !== null) {
      showHoveredColumn(scrollLeft, mouseXPositionRef.current);
    }

    // Sync position of current frame marker based on scroll position
    currentFrameMarkerRef.current?.sync(scrollLeft, true);
  }, [columnStripRef, rowStripRef, currentFrameMarkerRef, showHoveredColumn]);
  const handleGridScroll: OnScrollT = useCallback(event => {
    // Close edit annotation panel if it is open
    editAnnotationPanelRef.current?.close();
    scrollRef.current = {
      scrollLeft: event.scrollLeft,
      scrollTop: event.scrollTop
    };
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        sync(scrollRef.current.scrollLeft, scrollRef.current.scrollTop);
        rafRef.current = null;
      });
    }
  }, [rafRef, sync, editAnnotationPanelRef]);
  const handleBodyMouseMove: MouseEventHandler<HTMLDivElement> = useCallback(e => {
    if (!(e.currentTarget instanceof HTMLElement)) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    mouseXPositionRef.current = e.clientX - rect.left;
    const columnIndex = getFrameIndex({
      scrollLeft: scrollRef.current.scrollLeft,
      mouseXPosition: mouseXPositionRef.current,
      frameWidth: FRAME_WIDTH,
      maxFrames: totalFrames
    });
    hoveredColumnRef.current?.show(columnIndex, scrollRef.current.scrollLeft);
  }, [hoveredColumnRef, totalFrames]);
  const handleBodyMouseLeave = useCallback(() => {
    mouseXPositionRef.current = null;
    hoveredColumnRef.current?.hide();
  }, [hoveredColumnRef]);
  const getColumnIndexFromBodyClickEvent = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mousePositionX = e.clientX - rect.left;
    const columnIndex = getFrameIndex({
      scrollLeft: scrollRef.current.scrollLeft,
      mouseXPosition: mousePositionX,
      frameWidth: FRAME_WIDTH,
      maxFrames: totalFrames
    });
    return columnIndex;
  }, [totalFrames]);
  return {
    handleGridScroll,
    handleBodyMouseMove,
    handleBodyMouseLeave,
    getColumnIndexFromBodyClickEvent
  };
}